type ColorScale<T extends { readonly main: string }> = string & T

const createColorScale = <T extends { readonly main: string }>(steps: T): ColorScale<T> => {
    const toMain = () => steps.main
    const scale = (Object.assign(function ColorScale() {
        return steps.main
    }, steps) as unknown) as ColorScale<T>

    Object.defineProperties(scale, {
        toString: { value: toMain, enumerable: false },
        valueOf: { value: toMain, enumerable: false },
        [Symbol.toPrimitive]: { value: toMain, enumerable: false },
    })

    return scale
}

const white = {
    main: '#ffffff',
    700: '#ffffff',
    600: '#ffffffd9',
    500: '#ffffffa3',
    400: '#ffffff66',
    300: '#ffffff24',
    200: '#ffffff17',
    100: '#ffffff0f',
    50: '#ffffff08',
} as const

const black = {
    main: '#1a1a23',
    700: '#1a1a23',
    600: '#1a1a23f2',
    500: '#1a1a23d6',
    400: '#1a1a2399',
    300: '#1a1a235c',
    200: '#1a1a231f',
    100: '#1a1a2314',
    50: '#1a1a230a',
} as const

const neutral = {
    main: '#0f0f10',
    1000: '#000000',
    900: '#0f0f10',
    850: '#28282a',
    800: '#414144',
    700: '#5a5a5e',
    600: '#737378',
    500: '#8f8f98',
    400: '#b4b4b7',
    300: '#ceced0',
    200: '#dddddf',
    100: '#efeff0',
    50: '#f7f7f8',
    0: '#ffffff',
} as const

const brandBlue = {
    main: '#286cf2',
    900: '#1718d9',
    800: '#2d59df',
    700: '#286cf2',
    600: '#286cf2e0',
    500: '#286cf299',
    400: '#286cf266',
    300: '#286cf226',
    200: '#286cf21a',
    100: '#286cf20d',
} as const

const info = {
    main: '#26c6da',
    900: '#00828f',
    800: '#01a8c1',
    700: '#26c6da',
    600: '#26c6dae0',
    500: '#26c6da99',
    400: '#26c6da66',
    300: '#26c6da26',
    200: '#26c6da1a',
    100: '#26c6da0d',
} as const

const warning = {
    main: '#ffaf10',
    900: '#fd6c10',
    800: '#fe8c11',
    700: '#ffaf10',
    600: '#ffaf10e0',
    500: '#ffaf1099',
    400: '#ffaf1066',
    300: '#ffaf1026',
    200: '#ffaf101a',
    100: '#ffaf100d',
} as const

const error = {
    main: '#d92843',
    900: '#8f1a2a',
    800: '#b32237',
    700: '#d92843',
    600: '#d92843e0',
    500: '#d9284399',
    400: '#d9284366',
    300: '#d9284326',
    200: '#d928431a',
    100: '#d928430d',
} as const

const success = {
    main: '#00b55b',
    900: '#006630',
    800: '#009947',
    700: '#00b55b',
    600: '#00b55be0',
    500: '#00b55b99',
    400: '#00b55b66',
    300: '#00b55b26',
    200: '#00b55b1a',
    100: '#00b55b0d',
} as const

export const palette = {
    white: createColorScale(white),
    black: createColorScale(black),
    neutral: createColorScale(neutral),
    brandBlue: createColorScale(brandBlue),
    info: createColorScale(info),
    warning: createColorScale(warning),
    error: createColorScale(error),
    success: createColorScale(success),
}

export type Palette = typeof palette
